<script>
import Layout from '@layouts/main.vue'
import appConfig from '@src/app.config'
import validationMixin from '@components/mixins/validation'
import { mapState } from 'vuex'
import { authMethods, signinComputed, socialAuthMethods } from '@state/helpers'

export default {
  page: {
    title: 'Login',
    meta: [{ name: 'description', content: `Log in to ${appConfig.title}` }],
  },
  name: 'LogInView',
  components: { Layout },
  mixins: [validationMixin],
  data() {
    const checkValidUser = (rule, value, callback) => {
      callback(this.validateUser(rule, value, callback))
      resetForm()
    }

    const checkValidPass = (rule, value, callback) => {
      callback(this.validatePass(rule, value, callback))
      resetForm()
    }

    // Don't display any error messages
    const resetForm = () => {
      this.$refs.logInForm.clearValidate()
    }

    return {
      errorValidating: false,
      logInForm: {
        username: '',
        password: '',
      },
      formRules: {
        username: [{ validator: checkValidUser, trigger: 'blur' }],
        password: [{ validator: checkValidPass, trigger: 'blur' }],
      },
    }
  },
  computed: {
    ...signinComputed,
    placeholders() {
      return process.env.NODE_ENV === 'production'
          ? {
            username: 'Username or email',
            password: 'Password',
          }
          : {
            username: 'Use "admin" to log in with the mock API',
            password: 'Use "password" to log in with the mock API',
          }
    },
    ...mapState({
      serverError: (state) => state.auth.error,
      socialAuthErrorMessage: (state) => state.auth.socialAuthErrorMessage,
    }),
  },
  beforeCreate() {
    if (typeof window.Onvocado.recaptchaLoaded === 'undefined') {
      const script = document.createElement('script')
      script.src =
          'https://www.google.com/recaptcha/api.js?render=explicit'
      script.async = true
      script.defer = true
      document.body.appendChild(script)
      window.Onvocado.recaptchaLoaded = true
    }
    if (typeof window.Onvocado.googleGSIClient === 'undefined') {
      const script = document.createElement('script')
      script.src =
          'https://accounts.google.com/gsi/client'
      script.async = true
      script.defer = true
      document.body.appendChild(script)
      window.Onvocado.googleGSIClient = true
    }
  },
  mounted() {
    const gauthOptions = {
      clientId: process.env.VUE_APP_G_OAUTH_CLIENT_ID,
      scope: 'profile email',
      prompt: 'select_account',
    }

    // Function to initialize Google OAuth
    const initializeGoogleOAuth = () => {
      this.$nextTick(() => {
        window.google.accounts.id.initialize({
          client_id: gauthOptions.clientId,
          callback: this.handleCredentialResponse,
          auto_select: true,
        })

        const btnWidth = this.$refs.loginBtn?.offsetWidth

        window.google.accounts.id.renderButton(
            this.$refs.googleLoginBtn, {
              text: 'signin_with', // or 'signup_with' | 'continue_with' | 'signin_with' |  'signin'
              size: 'large', // or 'small' | 'medium'
              width: btnWidth, // max width 320
              shape: 'pill',
              theme: 'outline', // or 'filled_black' |  'filled_blue'
              logo_alignment: 'left', // or 'center'
            },
        )
      })
    }

    // Function to check if window.google is available and initialize OAuth
    const checkGoogleAvailability = () => {
      if (window.google && window.google.accounts) {
        initializeGoogleOAuth()
      } else {
        setTimeout(checkGoogleAvailability, 200) // Check again after 200 ms
      }
    }

    // Start the checking process
    checkGoogleAvailability()
  },
  methods: {
    ...authMethods,
    ...socialAuthMethods,
    // Try to log the user in with the username
    // and password they provided.
    formSubmit() {
      this.$refs.logInForm.validate((valid) => {
        if (valid) {
          this.logIn({
            username: this.logInForm.username,
            password: this.logInForm.password,
          }).then(() => {
            if (!this.serverError) {
              // Redirect to the originally requested page, or to the dashboard page
              this.logInRedirect()
            }
          })
        } else {
          this.errorValidating = true
        }
      })
    },
    // ToDo move this to a mixin ?
    async handleCredentialResponse(response) {
      const loginOptions = {
        provider: 'google',
        access_token: response.credential,
        id_token: response.clientId,
      }

      this.loginWith(loginOptions).then(() => {
        if (!this.serverError) {
          // Redirect to the originally requested page, or to the dashboard page
          // Magical timing of 600 milliseconds, d.k. why this works
          setTimeout(() => {
            this.$nextTick(() => {
              this.logInRedirect()
            })
          }, 600)
        }
      })
    },
    logInRedirect() {
      this.$router.push(this.$route.query.redirectFrom || { name: 'dashboard' })
    },
  },
}
</script>

<template>
  <Layout>
    <div :class="$style.logInView">
      <el-form
          ref="logInForm"
          :model="logInForm"
          class="w-full md:w-80 m-auto"
          :rules="formRules"
          label-width="85px"
          label-position="top"
          status-icon>
        <header>
          <img class="OnvocadoLogo" src="/onvocado_logo.png" alt="Onvocado Logo">
          <h3>Welcome to Onvocado</h3>
        </header>

        <div v-show="errorValidating || serverError"
             class="e-server-error">
          {{ socialAuthErrorMessage ? socialAuthErrorMessage : 'Incorrect username, email or password.' }}
        </div>

        <el-form-item
            class="basic"
            prop="username"
            :show-message="false">
          <el-input
              v-model="logInForm.username"
              type="text"
              name="username"
              :placeholder="placeholders.username"
              auto-complete="off"></el-input>
        </el-form-item>
        <el-form-item
            class="basic"
            prop="password"
            :show-message="false">
          <el-input
              v-model="logInForm.password"
              type="password"
              name="password"
              :placeholder="placeholders.password"
              auto-complete="off"
              @keyup.enter.native="formSubmit(logInForm)"></el-input>
        </el-form-item>
        <el-form-item>
          <button ref="loginBtn"
                  class="btn primary"
                  type="button"
                  :disabled="authenticationLoading"
                  @click.prevent="formSubmit(logInForm)">Log in
          </button>
        </el-form-item>

        <div :class="$style.or">
          <hr>
          <span>or</span></div>

        <el-form-item>
          <div ref="googleLoginBtn"/>
          <!--          <el-button-->
          <!--              type="primary"-->
          <!--              :class="$style.googleBtn"-->
          <!--              round-->
          <!--              @click.prevent="logInGoogle">-->
          <!--            <i :class="$style.gLogo"></i>-->
          <!--            Log in with Google-->
          <!--          </el-button>-->
        </el-form-item>

        <footer :class="$style.actions">
          <div>
            Forgot password?
            <router-link :to="{ name: 'password_reset' }">
              Reset it
            </router-link>
            .
          </div>
          <div>
            Don't have an account yet?
            <router-link :to="{ name: 'register' }" class="e-create-account">
              Sign up
            </router-link>
            .
          </div>
        </footer>
      </el-form>
    </div>
  </Layout>
</template>

<style lang="scss" module>
@import '@assets/sass/design/_mixin.scss';

.logInView {
  @include pageDefaults;
  @apply mx-4 md:mx-auto;

  height: calc(100vh - 60px);
  display: flex;
}

.actions {
  @apply text-xs text-onvocado-gray;

  display: flex;
  flex-direction: column;
  margin-bottom: 100px;

  div {
    @apply text-secondary;

    margin-bottom: 10px;
  }
}
</style>
